import { useCallback, useEffect, useState } from 'react'
import { useDemandViewLogic } from './logic'
import { DemandDetails } from '../components/DemandDetail/DemandDetails'
import { SideDemandTable } from '../components/SideDemandTable'
import { useDemand } from '../../../contexts'
import type { Demanda } from '../../../models'
import type { DemandListType } from '../hooks/useDemandNavigation'
import { Loading, LoadingInline } from '../../../components'
import { useKeyboardShortcut } from '../../../hooks/useKeyboardShortcut'
import { useKeyboardShortcutsContext } from '../../../contexts/KeyboardShortcutsContext'

export const DemandViewPage = () => {
  const {
    demanda,
    loading,
    error,
    currentIndex,
    totalDemandas,
    canNavigateNext,
    canNavigatePrevious,
    navigateToNextDemand,
    navigateToPreviousDemand,
    goBackToList,
    // Smart refresh props
    isRefreshing,
    smartRefresh,
    // Navegação de demandas
    navigation,
  } = useDemandViewLogic()

  const demandContext = useDemand()
  const { enabled: shortcutsEnabled } = useKeyboardShortcutsContext()

  // Estado de edição
  const [isEditing, setIsEditing] = useState(false)
  
  // Estado de visibilidade da barra lateral
  const [isSidebarVisible, setIsSidebarVisible] = useState(true)
  const [sidebarKey, setSidebarKey] = useState(0)

  // Keyboard shortcuts para a tela de detalhes
  // S - Toggle sidebar
  useKeyboardShortcut(
    's',
    () => {
      setIsSidebarVisible(prev => {
        const newValue = !prev;
        setSidebarKey(k => k + 1);
        return newValue;
      });
    },
    {
      enabled: shortcutsEnabled,
      description: 'Abrir/Fechar Sidebar',
      category: 'demand-view'
    }
  );

  const sideListLabels: Record<DemandListType, string> = {
    received: 'Demandas do setor (recebidas)',
    toAssign: 'Demandas em atribuição',
    sent: 'Demandas enviadas',
    watching: 'Demandas observadas',
    all: 'Todas as demandas',
  }

  // Função para alternar modo de edição
  const handleEditToggle = useCallback(() => {
    setIsEditing(!isEditing)
  }, [isEditing])

  // Função para lidar com salvamento
  // NOTA: O useDemandEdit já faz o update no backend, aqui apenas fazemos refresh
  const handleSave = async (_updatedDemanda: Partial<Demanda>) => {
    if (!demanda?.id) {
      console.error('[handleSave] Demanda ID não encontrado')
      return
    }
    
    try {
      // Usar smart refresh para atualização dos dados
      // O useDemandEdit já fez o update no backend via demandaService.update()
      await smartRefresh()
    } catch (error) {
      console.error('[handleSave] Erro ao salvar demanda:', error)
      throw error
    }
  }

  // Funções para SideDemandTable
  const handleSelectRelatedDemand = (selectedDemanda: Demanda) => {
    // Usar seleção local ao invés de navegação completa
    navigation.selectDemandLocally(selectedDemanda)

    // Atualizar URL sem recarregar (opcional, para manter histórico de navegação)
    const currentType = new URLSearchParams(window.location.search).get('type') || 'all'
    const newUrl = `/demands/${selectedDemanda.id}/view?type=${currentType}`
    window.history.pushState(null, '', newUrl)
  }

  const handleViewRelatedDemand = (viewDemanda: Demanda) => {
    navigation.viewDemandInNewTab(viewDemanda)
  }

  // Sempre mostrar SideDemandTable (será populado com dados conforme disponível)
  const shouldShowSideTable = true

  // Atualizar o contexto sempre que os dados mudarem
  useEffect(() => {
    demandContext.setDemandData({
      demanda,
      loading,
      error,
      currentIndex,
      totalDemandas,
      canNavigateNext,
      canNavigatePrevious,
    })
  }, [demanda, loading, error, currentIndex, totalDemandas, canNavigateNext, canNavigatePrevious, demandContext])

  // Definir callbacks de navegação
  useEffect(() => {
    demandContext.setNavigationCallbacks({
      onNavigateNext: navigateToNextDemand,
      onNavigatePrevious: navigateToPreviousDemand,
      onGoBack: goBackToList,
      onEditToggle: handleEditToggle,
    })
  }, [navigateToNextDemand, navigateToPreviousDemand, goBackToList, handleEditToggle, demandContext])

  // Limpar contexto ao desmontar o componente
  useEffect(() => {
    return () => {
      demandContext.clearDemand()
    }
  }, [demandContext])

  // Mostrar loading apenas no carregamento inicial (quando ainda não há demanda)
  if (loading && !demanda) {
    return (
      <div className='flex items-center justify-center min-h-64'>
        <Loading message='Carregando demanda...' />
      </div>
    )
  }

  if (error) {
    return (
      <div className='p-6'>
        <div className='bg-red-50 border border-red-200 rounded-lg p-4'>
          <div className='flex items-center'>
            <div className='text-red-700'>
              <strong>Erro ao carregar demanda:</strong> {error}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!demanda) {
    return (
      <div className='p-6'>
        <div className='bg-yellow-50 border border-yellow-200 rounded-lg p-4'>
          <div className='flex items-center'>
            <div className='text-yellow-700'>
              <strong>Demanda não encontrada</strong>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='flex gap-2 px-1 py-2'>
      {/* Coluna principal */}

      {/* Coluna lateral - SideDemandTable */}
      {shouldShowSideTable && (
        <>
          {isSidebarVisible ? (
            <div 
              key={`sidebar-expanded-${sidebarKey}`}
              className='w-96 flex-shrink-0 space-y-3'
              style={{
                animation: 'slideInFromLeft 0.3s ease-out forwards',
              }}
            >
              <div className='bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg p-3 shadow-sm'>
                <div className='flex flex-col space-y-2'>
                  <div className='flex items-center justify-between'>
                    <label
                      htmlFor='demand-view-side-filter'
                      className='text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide'
                    >
                      Filtrar demandas
                    </label>
                    <button
                      onClick={() => {
                        setIsSidebarVisible(false)
                        setSidebarKey(prev => prev + 1)
                      }}
                      className='p-1 rounded hover:bg-gray-100 dark:hover:bg-neutral-700 transition-colors group'
                      title='Ocultar barra lateral'
                    >
                      <svg className='w-4 h-4 text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 19l-7-7 7-7' />
                      </svg>
                    </button>
                  </div>
                  <select
                    id='demand-view-side-filter'
                    value={navigation.listType}
                    onChange={event => navigation.setListType(event.target.value as DemandListType)}
                    disabled={navigation.loadingAllDemandas}
                    className='w-full text-sm border border-gray-300 dark:border-neutral-600 rounded-md px-3 py-2 bg-white dark:bg-neutral-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-60'
                  >
                    {navigation.availableListTypes.map(type => (
                      <option key={type} value={type}>
                        {sideListLabels[type]}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <SideDemandTable
                demandas={navigation.allDemandas}
                selectedDemandId={navigation.selectedDemandId}
                onSelectDemand={handleSelectRelatedDemand}
                onViewDemand={handleViewRelatedDemand}
                isLoading={navigation.loadingAllDemandas}
                title={sideListLabels[navigation.listType]}
                className='sticky top-4'
                maxHeight='calc(100vh - 230px)'
                showSearch={true}
                showFilter={false}
              />
            </div>
          ) : (
            <div 
              key={`sidebar-collapsed-${sidebarKey}`}
              className='flex-shrink-0'
              style={{
                animation: 'slideInFromLeft 0.3s ease-out forwards',
              }}
            >
              <button
                onClick={() => {
                  setIsSidebarVisible(true)
                  setSidebarKey(prev => prev + 1)
                }}
                className='w-12 h-full min-h-[calc(100vh-120px)] bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl hover:bg-gray-50 dark:hover:bg-neutral-700 hover:scale-105 transition-all duration-300 ease-in-out flex flex-col items-center justify-center group shadow-sm hover:shadow-md sticky top-4'
                title='Mostrar filtros e lista de demandas'
              >
                <div className='flex flex-col items-center gap-3'>
                  <span className='text-xs font-semibold text-gray-700 dark:text-gray-300 [writing-mode:vertical-lr] rotate-180 tracking-wide'>
                    VER MAIS DEMANDAS
                  </span>
                  <svg className='w-5 h-5 text-gray-600 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
                  </svg>
                </div>
              </button>
            </div>
          )}
        </>
      )}

      <div className='flex-1 space-y-6'>
        {/* Indicador de atualização */}
        {isRefreshing && (
          <div className='fixed top-16 right-4 z-50 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 shadow-sm'>
            <div className='flex items-center space-x-2'>
              <LoadingInline size='xs' />
              <span className='text-sm text-blue-700'>Atualizando dados...</span>
            </div>
          </div>
        )}

        <DemandDetails
          demanda={demanda}
          isEditing={isEditing}
          onEditToggle={handleEditToggle}
          onSave={handleSave}
        />
      </div>
    </div>
  )
}
